import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
  MenuItem,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CategoryIcon from "@mui/icons-material/Category";
import InfoIcon from "@mui/icons-material/Info";
import SettingsSystemDaydreamIcon from "@mui/icons-material/SettingsSystemDaydream";
import GroupIcon from "@mui/icons-material/Group";
import EditIcon from "@mui/icons-material/Edit";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import dayjs from "dayjs";
import api from "../../api/client";
import { TruncatedText } from "../../components/TruncatedText";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { OverAllLoader } from "../../components/OverAllLoader";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";

const ViewCategory = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const location = useLocation();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { categoryData } = location.state || {};

  const [overAllLoading, setOverAllLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editCategoryId, setEditCategoryId] = useState(null);
  const [trainersList, setTrainersList] = useState([]);
  const [selectedTrainers, setSelectedTrainers] = useState([]);
  const [programsList, setProgramsList] = useState([]);
  const [instituteNames, setInstituteNames] = useState([]);
  const [viewData, setViewData] = useState(categoryData || null);
  const statuses = ["ACTIVE", "INACTIVE"];

  // Get current user role
  const userRole = localStorage.getItem("role");
  const isInstituteAdmin = userRole === "INSTITUTE_ADMIN";

  // Yup validation schema
  const schema = Yup.object().shape({
    category: Yup.string().required(`${getLabel('SUBJECT')} is required`),
    program_id: Yup.number().required("Program is required").typeError("Program is required"),
    category_outcome: Yup.string().required(`${getLabel('SUBJECT')} Outcome is required`),
    category_code: Yup.string()
      .required(`${getLabel('SUBJECT')} Code is required`)
      .max(50, "Max 50 characters"),
    institute_id: isInstituteAdmin
      ? Yup.mixed().notRequired()
      : Yup.number()
        .typeError(`${getLabel('INSTITUTE')} is required`)
        .required(`${getLabel('INSTITUTE')} is required`),
  });

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      category: "",
      status: "ACTIVE",
      category_outcome: "",
      program_id: null,
      category_code: "",
      institute_id: null,
    },
  });

  const fetchInstituteNames = async () => {
    try {
      const { data } = await api.post("ListInstituteNames");
      setInstituteNames(data?.data ?? []);
    } catch (e) {
      console.error("Error fetching institutes:", e);
    }
  };

  const fetchProgramsDropdown = async () => {
    try {
      const userStr = localStorage.getItem("userData");
      const user = userStr ? JSON.parse(userStr) : null;
      let institute_id = user?.role === "INSTITUTE_ADMIN" ? user.institute_id : null;

      const payload = {};
      if (institute_id) {
        payload.institute_id = institute_id;
      }

      const res = await api.post("GetProgramsDropdown", payload);
      if (res?.data?.status === "success") {
        setProgramsList(res?.data?.data || []);
      }
    } catch (error) {
      console.error("Error fetching programs:", error);
    }
  };

  useEffect(() => {
    fetchProgramsDropdown();
    if (!isInstituteAdmin) {
      fetchInstituteNames();
    }
    refreshViewData();
  }, []);

  const trainerList = async () => {
    try {
      const userStr = localStorage.getItem("userData");
      const user = userStr ? JSON.parse(userStr) : null;
      const institute_id =
        user?.role === "INSTITUTE_ADMIN" ? user.institute_id : 0;

      const payload = {
        institute_id: institute_id,
      };
      const res = await api.post("/ListTrainersForFilter", payload);
      if (res?.data?.status === "success") {
        setTrainersList(res?.data?.data);
      }
    } catch (error) {
      console.log("Error :", error);
    }
  };

  const handleEdit = () => {
    const categoryId = viewData?.category_id;
    if (!categoryId) return;

    setEditCategoryId(categoryId);
    setOverAllLoading(true);

    api
      .post("ViewCategoryByID", { category_id: categoryId })
      .then((res) => {
        if (res?.data?.status === "success") {
          const data = res.data.data;
          const trainerIds = data.assignedTrainersIds || [];
          const trainerNames = data.assignedTrainersName || [];

          const trainers = trainerIds.map((id, index) => ({
            trainer_id: id,
            name: trainerNames[index] || "",
          }));

          // Fall back to viewData (list-row) institute_id if view endpoint omits it
          const resolvedInstituteId =
            data.institute_id ?? viewData?.institute_id ?? null;

          reset({
            category: data.category_name,
            assignTrainer: trainerIds,
            status: data.status,
            category_outcome: data.category_outcome || "",
            program_id: data.program_id || null,
            category_code: data.category_code || "",
            institute_id: resolvedInstituteId,
          });

          setSelectedTrainers(trainers);
          trainerList();
          setOpenForm(true);
        } else {
          showSnackbar("error", res?.data?.message);
        }
      })
      .catch((error) => {
        console.error("Error fetching category:", error);
        showSnackbar(
          "error",
          error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch subject details."
        );
      })
      .finally(() => {
        setOverAllLoading(false);
      });
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    reset();
  };

  const onSubmit = async (data) => {
    setOverAllLoading(true);
    try {
      const userStr = localStorage.getItem("userData");
      const user = userStr ? JSON.parse(userStr) : null;
      const institute_id = isInstituteAdmin
        ? user?.institute_id || null
        : data.institute_id;

      const updatePayload = {
        category_id: editCategoryId,
        institute_id: institute_id,
        category: data.category,
        status: data.status,
        trainersIds: isInstituteAdmin ? data.assignTrainer || [] : null,
        category_outcome: data.category_outcome || "",
        program_id: data.program_id || 0,
        ...(data.category_code ? { category_code: data.category_code } : {}),
      };

      const res = await api.post("UpdateCategoryByID", updatePayload);
      if (res?.data?.status === "success") {
        showSnackbar("success", res?.data?.message);
        setOpenForm(false);
        // Refresh the view data after update
        refreshViewData();
      } else {
        showSnackbar("error", res?.data?.message);
      }
    } catch (e) {
      console.log("Error:", e);
      showSnackbar(
        "error",
        e?.response?.data?.message ||
        e?.message ||
        "Something went wrong. Please try again."
      );
    } finally {
      setOverAllLoading(false);
    }
  };

  const refreshViewData = async () => {
    try {
      const categoryId = viewData?.category_id;
      if (!categoryId) return;

      const res = await api.post("ViewCategoryByID", {
        category_id: categoryId,
      });
      if (res?.data?.status === "success") {
        const data = res.data.data;
        setViewData({
          ...viewData,
          category_name: data.category_name,
          status: data.status,
          created_by: data.created_by || viewData?.created_by,
          created_at: data.created_at || viewData?.created_at,
          trainer_names: data.assignedTrainersName || [],
          program_name: data.program_name || viewData?.program_name,
          category_outcome: data.category_outcome || viewData?.category_outcome,
          category_code: data.category_code ?? viewData?.category_code ?? null,
          institute_id: data.institute_id ?? viewData?.institute_id ?? null,
          question_type_names: data.question_type_names || [],
          lab_tool_names: data.lab_tool_names || [],
        });
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  };

  if (!categoryData) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          No subject data found.
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {overAllLoading && <OverAllLoader message="Please wait..." />}
      <Box>
        {/* Back Button */}
        <Grid
          item
          sx={{
            display: "flex",
            alignItems: "center",
            color: "#1c97e3",
            gap: "4px",
            cursor: "pointer",
            width: "fit-content",
            mb: 2,
          }}
          onClick={() => navigate(-1)}
        >
          <ArrowBackIcon sx={{ fontSize: "17px" }} />
          <Typography
            sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}
          >
            Back
          </Typography>
        </Grid>

        {/* View Card */}
        <Box>
          {/* Header */}
          <Grid
            sx={{
              display: "flex",
              justifyContent: "space-between",
              borderBottom: "1px solid #d1d1d1",
            }}
          >
            <Grid
              sx={{
                mb: 2,
                display: "flex",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Grid
                sx={{
                  backgroundColor: "#1c97e3",
                  p: 0.5,
                  borderRadius: "10px",
                }}
              >
                <CategoryIcon sx={{ color: "white", fontSize: "35px" }} />
              </Grid>
              <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>
                {getLabel('SUBJECT')} Details
              </Typography>
            </Grid>
            <Grid sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="contained"
                className="gradient-button"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                Edit
              </Button>
            </Grid>
          </Grid>

          {/* Details */}
          <Box
            sx={{ p: 3, display: "flex", flexDirection: "column", rowGap: 3 }}
          >
            {/* Basic Info */}
            <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <InfoIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
              <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                Basic Information
              </Typography>
            </Grid>

            <Grid sx={{ display: "flex", flexDirection: "column", rowGap: 2 }}>
              {/* Row 1: Identity — Category Name + Category Code */}
              <Grid sx={{ display: "flex" }}>
                <Grid item xs={6} sx={{ width: "50%", minWidth: 0, pr: 2 }}>
                  <Typography fontWeight="600">{getLabel('SUBJECT')} Name</Typography>
                  <TruncatedText text={viewData.category_name || "-"} />
                </Grid>
                <Grid item xs={6} sx={{ width: "50%" }}>
                  <Typography fontWeight="600">{getLabel('SUBJECT')} Code</Typography>
                  {viewData.category_code ? (
                    <Box
                      sx={{
                        display: "inline-block",
                        px: 1.2,
                        py: 0.3,
                        borderRadius: "8px",
                        fontWeight: 600,
                        fontSize: "12px",
                        backgroundColor: "#e4eefb",
                        color: "#1c97e3",
                        letterSpacing: "0.5px",
                        mt: 0.5,
                      }}
                    >
                      {viewData.category_code}
                    </Box>
                  ) : (
                    <Typography>-</Typography>
                  )}
                </Grid>
              </Grid>
              {/* Row 2: Context + State — Program + Status */}
              <Grid sx={{ display: "flex" }}>
                <Grid item xs={6} sx={{ width: "50%", minWidth: 0, pr: 2 }}>
                  <Typography fontWeight="600">{getLabel('PROGRAMS')}</Typography>
                  <TruncatedText text={viewData.program_name || "-"} />
                </Grid>
                <Grid item xs={6} sx={{ width: "50%" }}>
                  <Typography fontWeight="600">Status</Typography>
                  <Typography>{viewData.status || "-"}</Typography>
                </Grid>
              </Grid>
              {/* Row 3: Detail — Category Outcome */}
              <Grid sx={{ display: "flex" }}>
                <Grid item xs={12} sx={{ width: "100%" }}>
                  <Typography fontWeight="600">{getLabel('SUBJECT_OUTCOME')}</Typography>
                  <Typography>{viewData.category_outcome || "-"}</Typography>
                </Grid>
              </Grid>
              {/* Row 4: Question Type + Tool / Lab Requirement set on the subject */}
              <Grid sx={{ display: "flex" }}>
                <Grid item xs={6} sx={{ width: "50%", minWidth: 0, pr: 2 }}>
                  <Typography fontWeight="600">Question Type</Typography>
                  <Typography>{viewData.question_type_names?.length ? viewData.question_type_names.join(", ") : "-"}</Typography>
                </Grid>
                <Grid item xs={6} sx={{ width: "50%" }}>
                  <Typography fontWeight="600">Tool / Lab Requirement</Typography>
                  <Typography>{viewData.lab_tool_names?.length ? viewData.lab_tool_names.join(", ") : "-"}</Typography>
                </Grid>
              </Grid>
            </Grid>

            {/* Trainers Info */}
            <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <GroupIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
              <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                Assigned {getLabel('TRAINER')}
              </Typography>
            </Grid>

            <Grid sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {(() => {
                const names =
                  viewData.trainer_names || viewData.assignedTrainersName || [];
                return names.length > 0 ? (
                  <Typography>{names.join(", ")}</Typography>
                ) : (
                  <Typography>-</Typography>
                );
              })()}
            </Grid>

            {/* System Info — moved to bottom (audit metadata) */}
            <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SettingsSystemDaydreamIcon
                sx={{ color: "#1c97e3", fontSize: "30px" }}
              />
              <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                System Information
              </Typography>
            </Grid>

            <Grid sx={{ display: "flex" }}>
              <Grid item xs={6} sx={{ width: "50%" }}>
                <Typography fontWeight="600">Created By</Typography>
                <Typography>{viewData.created_by || "-"}</Typography>
              </Grid>
              <Grid item xs={6} sx={{ width: "50%" }}>
                <Typography fontWeight="600">Created Date</Typography>
                <Typography>
                  {viewData.created_at
                    ? dayjs(viewData.created_at).format("DD-MM-YYYY")
                    : "-"}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>

      {/* Edit Dialog */}
      <Dialog
        open={openForm}
        PaperProps={{
          sx: { width: "500px", borderRadius: "12px", p: 2 },
        }}
      >
        <Box
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
          <CategoryIcon
            sx={{
              backgroundColor: "#1976d2",
              color: "white",
              fontSize: "30px",
              top: -20,
              borderRadius: "50%",
              padding: "5px",
            }}
          />
          <DialogTitle
            sx={{ fontWeight: 700, color: "#1976d2", padding: "5px" }}
          >
            {`Update ${getLabel('SUBJECT')}`}
          </DialogTitle>
        </Box>
        <Divider
          sx={{ mt: 2, borderColor: "black", borderBottomWidth: 1, mx: 0 }}
        />
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1 }}>
            {/* 1. Program */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontWeight: "bold", mb: 0.5 }}>
                Program <span style={{ color: "red" }}>*</span>
              </Typography>
            </Box>
            <Controller
              name="program_id"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={programsList}
                  getOptionLabel={(option) => {
                    if (!option) return "";
                    const label = option.display_name || option.program_name || "";
                    return option.program_code ? `[${option.program_code}] ${label}` : label;
                  }}
                  value={
                    programsList.find((p) => p.program_id === field.value) || null
                  }
                  onChange={(e, newValue) => {
                    field.onChange(newValue ? newValue.program_id : null);
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.program_id}>
                      {option.program_code ? `[${option.program_code}] ` : ""}
                      {option.display_name || option.program_name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      placeholder="Select Program"
                      margin="dense"
                      sx={{ mb: 2 }}
                      error={!!errors.program_id}
                      helperText={errors.program_id?.message}
                    />
                  )}
                />
              )}
            />

            {/* 2. Subject */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                mt: 1,
              }}
            >
              <Typography sx={{ fontWeight: "bold", mb: 0.5 }}>
                {`${getLabel('SUBJECT')}`} <span style={{ color: "red" }}>*</span>
              </Typography>
            </Box>
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <TextField
                  sx={{ mb: 2 }}
                  {...field}
                  fullWidth
                  placeholder={`Enter ${getLabel('SUBJECT')}`}
                  margin="dense"
                  error={!!errors.category}
                  helperText={errors.category?.message}
                  inputProps={{
                    style: { textTransform: "uppercase" },
                  }}
                  onChange={(e) => {
                    const uppercaseValue = e.target.value.toUpperCase();
                    field.onChange(uppercaseValue);
                  }}
                />
              )}
            />

            {/* 3. Subject Code */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontWeight: "bold", mb: 0.5 }}>
                {`${getLabel('SUBJECT')} Code`} <span style={{ color: "red" }}>*</span>
              </Typography>
            </Box>
            <Controller
              name="category_code"
              control={control}
              render={({ field }) => (
                <TextField
                  sx={{ mb: 2 }}
                  {...field}
                  fullWidth
                  placeholder="e.g. JAVA01 (max 50 chars)"
                  margin="dense"
                  inputProps={{ maxLength: 50 }}
                  error={!!errors.category_code}
                  helperText={errors.category_code?.message}
                />
              )}
            />

            {/* 4. Subject Outcome */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontWeight: "bold", mb: 0.5 }}>
                {`${getLabel('SUBJECT')} Outcome`} <span style={{ color: "red" }}>*</span>
              </Typography>
            </Box>
            <Controller
              name="category_outcome"
              control={control}
              render={({ field }) => (
                <TextField
                  sx={{ mb: 2 }}
                  {...field}
                  fullWidth
                  placeholder={`Enter ${getLabel('SUBJECT')} Outcome`}
                  margin="dense"
                  multiline
                  minRows={3}
                  error={!!errors.category_outcome}
                  helperText={errors.category_outcome?.message}
                />
              )}
            />

            {/* 5. Assign Institute — Super Admin only */}
            {!isInstituteAdmin && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    mt: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: "bold", mb: 0.5 }}>
                    {`Assign ${getLabel('INSTITUTE')}`} <span style={{ color: "red" }}>*</span>
                  </Typography>
                </Box>
                <Controller
                  name="institute_id"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={instituteNames}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(o, v) =>
                        o?.institute_id === v?.institute_id
                      }
                      value={
                        instituteNames.find(
                          (inst) => inst.institute_id === field.value
                        ) || null
                      }
                      onChange={(_, newValue) =>
                        field.onChange(newValue ? newValue.institute_id : null)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder={`Select ${getLabel('INSTITUTE')}`}
                          margin="dense"
                          sx={{ mb: 2 }}
                          error={!!errors.institute_id}
                          helperText={errors.institute_id?.message}
                        />
                      )}
                    />
                  )}
                />
              </>
            )}

            {/* 6. Assign Trainers - Only for Institute Admin */}
            {isInstituteAdmin && (
              <Grid sx={{ width: "100%" }}>
                <Typography sx={{ fontWeight: "bold", mb: 1 }}>
                  Assign Trainers
                </Typography>

                <Controller
                  name="assignTrainer"
                  control={control}
                  defaultValue={[]}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      options={trainersList}
                      getOptionLabel={(option) => option.name || ""}
                      value={trainersList.filter((t) =>
                        field.value?.includes(t.trainer_id)
                      )}
                      onChange={(_, selectedValues) => {
                        const selectedIds = selectedValues.map(
                          (v) => v.trainer_id
                        );
                        field.onChange(selectedIds);
                        setSelectedTrainers(selectedValues);
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option.trainer_id === value.trainer_id
                      }
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            checked={selected}
                            readOnly
                            sx={{ mr: 1 }}
                          />
                          {option.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={`Select ${getLabel('TRAINER')}(s)`}
                          fullWidth
                          error={!!errors.assignTrainer}
                          helperText={errors.assignTrainer?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            )}

            {/* Status */}
            <Grid sx={{ display: "flex", gap: 3 }}>
              <Grid sx={{ width: "100%", mt: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  {watch("status") === "ACTIVE" ? (
                    <ToggleOnIcon
                      sx={{
                        color: "#1976d2",
                        fontSize: "25px",
                        mr: 1,
                      }}
                    />
                  ) : (
                    <ToggleOffIcon
                      sx={{
                        color: "grey",
                        fontSize: "25px",
                        mr: 1,
                      }}
                    />
                  )}
                  <Typography sx={{ fontWeight: "bold" }}>
                    Status <span style={{ color: "red" }}>*</span>
                  </Typography>
                </Box>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} select fullWidth>
                      <MenuItem
                        value="default"
                        disabled
                        sx={{ color: "grey" }}
                      >
                        Select Status
                      </MenuItem>
                      {statuses.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
            </Grid>

            <DialogActions sx={{ justifyContent: "end", pb: 2, mt: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCloseForm}
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ textTransform: "none" }}
              >
                {`Update ${getLabel('SUBJECT')}`}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ViewCategory;
